import React, { useEffect, useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { JdsButton } from "../../../components/jds/JdsButton";
import { CloseIcon } from "../../../components/jds/icons";
import { jdsSelectProps } from "../../../components/jds/selectProps";
import "../../../components/jds/jds.css";
import "../clienteDados.css";
import { useSelector } from "react-redux";

const formatBRL = (value) =>
  typeof value === "number"
    ? value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

function CardBrandIcon({ brand }) {
  const paymentMethodsRelation = useSelector(
    state => state.app.payment_methods_relation || []
  );
  const paymentMethod = paymentMethodsRelation.find(
    relation => relation.id === brand?.toLowerCase()
  );
  if (!paymentMethod) return null;
  return (
    <img alt={brand} src={paymentMethod.svg} style={{ height: "23px", width: "auto" }} />
  );
}

function CardOptionLabel({ card }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <CardBrandIcon brand={card.brand} />
      <span>{`${card.brand} •••• ${card.last_digits} — Val: ${card.expiration_date}`}</span>
    </div>
  );
}

function PreviewRow({ label, value, highlight }) {
  return (
    <div className="jds-detail-row">
      <span className="text-muted">{label}</span>
      <span
        className={highlight ? "cd-detail-value--highlight" : undefined}
        style={{ fontWeight: "var(--fw-semibold)" }}
      >
        {value}
      </span>
    </div>
  );
}

export default function UpgradePlanModal({
  show,
  onClose,
  onConfirm,
  loadPreview,
  creditCards = [],
  isLoadingCards = false,
  newPlanValue = 99
}) {
  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    setSelectedCard(null);
    setIsLoadingPreview(true);
    (async () => {
      try {
        const data = await loadPreview();
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setPreviewError(err);
      } finally {
        if (!cancelled) setIsLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, loadPreview]);

  const handleClose = () => {
    if (isSubmitting) return;
    setPreview(null);
    setPreviewError(null);
    setSelectedCard(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedCard || !preview?.canUpgrade) return;
    setIsSubmitting(true);
    try {
      await onConfirm({ cardId: selectedCard.hash, planValue: preview?.newPlanValue ?? newPlanValue });
    } catch (err) {
      console.error("Erro ao confirmar upgrade:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled =
    isSubmitting || isLoadingPreview || !selectedCard || !preview?.canUpgrade;

  return (
    <Modal show={show} onHide={handleClose} centered className="jds-scope">
      <Modal.Header>
        <Modal.Title>Upgrade da IA WhatsApp</Modal.Title>
        <JdsButton
          className="cd-modal-close"
          variant="ghost"
          color="neutral"
          size="sm"
          iconOnly
          onClick={handleClose}
          aria-label="Fechar"
        >
          <CloseIcon />
        </JdsButton>
      </Modal.Header>

      <Modal.Body>
        {isLoadingPreview && (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
            <Spinner animation="border" style={{ color: "var(--fg-brand-strong)" }} />
          </div>
        )}

        {!isLoadingPreview && previewError && (
          <div className="cd-alert cd-alert--error" role="alert">
            Não foi possível carregar o preview do upgrade. Tente novamente.
          </div>
        )}

        {!isLoadingPreview && preview && (
          <>
            <p className="text-muted">
              Revise os valores do upgrade do plano antes de confirmar a cobrança.
            </p>

            <div className="cd-preview">
              <PreviewRow label="Plano atual" value={`${preview.currentPlan ?? "—"} (${formatBRL(preview.currentPlanValue)})`} />
              <PreviewRow label="Novo plano" value={`${preview.newPlan ?? "—"} (${formatBRL(preview.newPlanValue)})`} />
              <PreviewRow label="Dias usados" value={preview.daysUsed ?? "—"} />
              <PreviewRow label="Dias restantes" value={preview.daysRemaining ?? "—"} />
              <PreviewRow label="Crédito restante" value={formatBRL(preview.creditRemaining)} />
              <PreviewRow label="Valor a cobrar" value={formatBRL(preview.chargeValue)} highlight />
            </div>

            {!preview.canUpgrade && (
              <div className="cd-alert" role="alert">
                {preview.message || "Upgrade não disponível no momento."}
              </div>
            )}

            <Form.Group>
              <Form.Label>Cartão de crédito</Form.Label>
              <Select
                name="card"
                options={creditCards}
                value={selectedCard}
                onChange={setSelectedCard}
                isLoading={isLoadingCards}
                placeholder="Selecione um cartão..."
                getOptionLabel={(card) => `${card.brand} •••• ${card.last_digits} — Val: ${card.expiration_date}`}
                formatOptionLabel={(card) => <CardOptionLabel card={card} />}
                getOptionValue={(card) => card.hash}
                noOptionsMessage={() => <span>Nenhum cartão cadastrado.</span>}
                {...jdsSelectProps}
                isClearable={false}
                isDisabled={!preview.canUpgrade}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <JdsButton
          variant="outlined"
          color="neutral"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancelar
        </JdsButton>
        <JdsButton onClick={handleConfirm} disabled={isConfirmDisabled}>
          {isSubmitting ? "Cobrando..." : "Cobrar e fazer upgrade"}
        </JdsButton>
      </Modal.Footer>
    </Modal>
  );
}
